import { twMerge } from '@/lib/classname/twMerge'
import { StatusDot, type StatusDotVariant } from '@/ui/status-dot/StatusDot'
import type { ComponentPropsWithoutRef, ReactElement, ReactNode } from 'react'

export type StatusLineProps = UiComponentProps<
  {
    /** Primary descriptive text */
    children: ReactNode
    /** Secondary line shown below the primary line */
    additionalText?: string
    /** Optional className for positioning only; internal styling is preserved */
    className?: string
  } & (
    | { variant: StatusDotVariant; icon?: never }
    | { variant?: StatusDotVariant; icon: ReactElement }
  )
> &
  StrictPick<ComponentPropsWithoutRef<'div'>, 'aria-label' | 'aria-labelledby'>

export function StatusLine({
  variant,
  icon,
  children,
  additionalText,
  className,
  id,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
}: StatusLineProps) {
  const resolvedVariant: StatusDotVariant = variant ?? 'default'

  const hasSecondary = additionalText != null && additionalText.trim().length > 0

  return (
    <div
      id={id}
      data-testid={dataTestId ?? 'status-line'}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={twMerge(
        className,
        'grid grid-cols-[auto_minmax(0,1fr)] gap-x-0.5 gap-y-0 text-brand-2-900',
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-[1lh] shrink-0 items-center justify-center self-start leading-light [&_svg]:block"
      >
        {icon != null ? icon : <StatusDot variant={resolvedVariant} />}
      </span>
      <div className="flex min-w-0 flex-wrap items-center gap-0.5 leading-light">
        <span className="min-w-0 text-ellipsis font-medium">{children}</span>
      </div>
      {hasSecondary && (
        <p className="col-start-2 m-0 text-sm font-medium leading-lighter">{additionalText}</p>
      )}
    </div>
  )
}
